import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Flex, Heading, Text, IconButton, Accordion, Table, Clipboard } from "@chakra-ui/react";
import { Cartesian3, JulianDate, Property, ScreenSpaceEventType, Color, PolylineDashMaterialProperty, PropertyBag } from "cesium";

import { computeZoomLevel } from "../utilies/MapUtility";
import { removeEntities, removeFileExtension, showToast } from "../utilies/Utility";
import { LuTrash, LuEye, LuEyeOff } from "react-icons/lu";
import { useAppStore } from "../context/store";
import Type from "../utilies/Types";
import { useMapObjects } from "../context/MapObjectsContext";
import { createToggleAssetVisibility } from "../utilies/other/ToggleAssetVisibility";

function isCesiumPropertyLike(v) {
  return v instanceof Property || (v && typeof v.getValue === "function");
}

function getPlainValue(v) {
  try {
    if (isCesiumPropertyLike(v)) return v.getValue?.(JulianDate.now());
    return v;
  } catch {
    return undefined;
  }
}

function computePolylineLengthMeters(entity) {
  try {
    const positions = entity?.polyline?.positions?.getValue?.(JulianDate.now());
    if (!Array.isArray(positions) || positions.length < 2) return 0;
    let len = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      len += Cartesian3.distance(positions[i], positions[i + 1]);
    }
    return len;
  } catch {
    return 0;
  }
}

function buildSections(entities) {
  const sections = [];
  const now = JulianDate.now();

  for (const entity of entities || []) {
    if (!entity) continue;

    // Ignore generated temp entities
    if (typeof entity.id === "string" && entity.id.startsWith("gje-")) continue;

    const props = entity.properties;

    // Cesium public API is propertyNames (not _propertyNames)
    const propNames =
      props?.propertyNames ??
      props?._propertyNames ?? // fallback
      Object.keys(props?.getValue?.(now) ?? {});

    const dataSource = entity.entityCollection?.owner;
    const layerName = dataSource?.layerName || "Unknown Layer";

    const rows = [];
    for (const key of propNames || []) {
      if (key === "node" || key === "nodes" || key === "trace") continue;
      const val = props?.[key];
      if (!val) continue;
      rows.push({ key, value: getPlainValue(val) });
    }

    // Always show id first
    rows.sort((a, b) => {
      if (a.key === "id") return -1;
      if (b.key === "id") return 1;
      return 0;
    });

    sections.push({
      sectionId: `infobox-sec-${sections.length}`,
      entity,
      layerName: removeFileExtension(layerName),
      lengthMeters: computePolylineLengthMeters(entity),
      rows,
    });
  }

  return sections;
}

function ValueView({ value, level = 1 }) {
  if (value === null || value === undefined) {
    return (
      <Text as="i" color="gray.500">
        null
      </Text>
    );
  }

  if (Array.isArray(value)) {
    if (level > 10) return <Text as="i">[Array]</Text>;
    return (
      <Table.Root size="sm" variant="outline" width="100%">
        <Table.Body>
          {value.map((item, i) => (
            <Table.Row key={i}>
              <Table.Cell fontWeight="bold" whiteSpace="nowrap">
                [{i}]
              </Table.Cell>
              <Table.Cell>
                <ValueView value={item} level={level + 1} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    );
  }

  if (typeof value === "object") {
    if (level > 10) return <Text as="i">[Object]</Text>;

    const plain = getPlainValue(value) ?? value;
    const entries = plain && typeof plain === "object" ? Object.entries(plain) : [];

    if (!entries.length) return <Text as="i">[Empty Object]</Text>;

    return (
      <Table.Root size="sm" variant="outline" width="100%">
        <Table.Body>
          {entries.map(([k, v]) => (
            <Table.Row key={k}>
              <Table.Cell fontWeight="bold" whiteSpace="nowrap">
                {k}
              </Table.Cell>
              <Table.Cell>
                <ValueView value={v} level={level + 1} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    );
  }

  return <Text>{String(value)}</Text>;
}

function applyLaneMarkerVisual(ent) {
  if (!ent?.polyline) return;

  const c = String(getPlainValue(ent.properties?.color) || "").toLowerCase();
  const s = String(getPlainValue(ent.properties?.subtype) || "").toLowerCase();

  const cesColor = c.includes("yellow") ? Color.YELLOW : Color.WHITE;

  if (s.includes("dashed")) {
    ent.polyline.material = new PolylineDashMaterialProperty({
      color: cesColor,
      dashLength: 16.0,
    });
  } else {
    ent.polyline.material = cesColor;
  }
}

export const CustomInfobox = forwardRef(function CustomInfobox(
  { viewer, editModeRef, associationRef, createLaneletModeRef, isEnableSelectingCommentRef, canOpenInfoboxRef, generateArcRef, setMapObjects },
  ref,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [expanded, setExpanded] = useState([]); // array of sectionId

  const highlightToolsRef = useAppStore.getState().highlightToolsRef;

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openForEntities = useCallback((entities) => {
    const built = buildSections(entities);
    setSections(built);

    if (!built.length) {
      setIsOpen(false);
      setExpanded([]);
      return;
    }

    setIsOpen(true);
    setExpanded(built.length === 1 ? [built[0].sectionId] : []);
  }, []);

  const openForEntity = useCallback(
    (entity) => {
      if (!entity) {
        setSections([]);
        setIsOpen(false);
        setExpanded([]);
        return;
      }
      openForEntities([entity]);
    },
    [openForEntities],
  );

  useImperativeHandle(
    ref,
    () => ({
      openForEntity,
      close,
    }),
    [openForEntity, close],
  );

  const { mapObjects } = useMapObjects();

  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const defaultOsmStyle = useAppStore((s) => s.defaultOsmStyle);
  const selectedProject = useAppStore((s) => s.selectedProject);
  const setMapObjectsAssest = useAppStore((s) => s.setMapObjectsAssest);

  // viewerRef-like for the shared toggle function
  const viewerRefLike = useMemo(() => ({ current: viewer }), [viewer]);

  const toggleAssetVisibility = useMemo(() => {
    return createToggleAssetVisibility({
      viewerRef: viewerRefLike,
      setMapObjects,

      defaultGeojsonStyle,
      basicGeojsonStyle,
      defaultOsmStyle,
      setMapObjectsAssest,
      selectedProject,

      // not available here; keep them off
      toggleLabelVisibility: undefined,
      toggleDirectionalArrows: undefined,
      directionArrowSettingRef: undefined,
      scanOn: false,
      toggleScanRoute: undefined,
    });
  }, [viewerRefLike, setMapObjects, defaultGeojsonStyle, basicGeojsonStyle, defaultOsmStyle, setMapObjectsAssest, selectedProject]);

  const onToggleVisibility = useCallback(
    (sec) => {
      if (!sec?.entity) return;

      const dsOwner = sec.entity?.entityCollection?.owner; // DataSource
      const layerName = sec.layerName; // already removeFileExtension() in buildSections

      const item =
        (Array.isArray(mapObjects) && mapObjects.find((o) => o?.dataSource === dsOwner || o?.osmDataSource === dsOwner)) ||
        (Array.isArray(mapObjects) && mapObjects.find((o) => removeFileExtension(o?.file_name || "") === layerName)) ||
        null;

      if (!item) return;

      toggleAssetVisibility(item);
    },
    [mapObjects, toggleAssetVisibility],
  );

  const onDelete = useCallback(
    (sectionId) => {
      setSections((prev) => {
        const sec = prev.find((s) => s.sectionId === sectionId);
        if (sec?.entity && viewer) {
          removeEntities(viewer, [sec.entity], setMapObjects);
          highlightToolsRef.current?.();
        }

        const next = prev.filter((s) => s.sectionId !== sectionId);
        if (!next.length) setIsOpen(false);

        setExpanded((old) => old.filter((v) => v !== sectionId));
        return next;
      });
    },
    [viewer, setMapObjects, highlightToolsRef],
  );

  useEffect(() => {
    if (!viewer) return;
    if (typeof viewer.isDestroyed === "function" && viewer.isDestroyed()) return;

    // Hide default Cesium infobox
    try {
      if (viewer.infoBox?.container) {
        viewer.infoBox.container.style.display = "none";
      }
    } catch {}

    const handler = (click) => {
      const blocked =
        editModeRef?.current || associationRef?.current || createLaneletModeRef?.current || isEnableSelectingCommentRef?.current || !canOpenInfoboxRef?.current || generateArcRef?.current;

      if (blocked) {
        close();
        return;
      }

      if (computeZoomLevel(viewer) < 12) {
        close();
        return;
      }

      const picked = viewer.scene.pick(click.position);
      if (picked?.id && viewer.measurementEntities?.includes(picked.id)) return;

      const pickedObjects = viewer.scene.drillPick(click.position);
      if (!pickedObjects?.length) {
        viewer.selectedEntity = undefined;
        setSections([]);
        close();
        return;
      }

      const entities = pickedObjects.map((p) => p?.id).filter(Boolean);
      if (!entities.length) {
        setSections([]);
        close();
        return;
      }

      openForEntities(entities);
    };

    let sseh = null;
    try {
      sseh = viewer.screenSpaceEventHandler;
    } catch {
      sseh = null;
    }
    if (!sseh) return;

    try {
      sseh.setInputAction(handler, ScreenSpaceEventType.LEFT_CLICK);
    } catch {}

    return () => {
      try {
        if (sseh && typeof sseh.isDestroyed === "function" && sseh.isDestroyed()) return;
        sseh.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
      } catch {}
    };
  }, [viewer, editModeRef, associationRef, createLaneletModeRef, isEnableSelectingCommentRef, canOpenInfoboxRef, generateArcRef, close, openForEntities]);

  useEffect(() => {
    if (!viewer) return;

    const onKeyDown = (evt) => {
      if (!isOpen) return;
      if (sections.length !== 1) return;

      const tag = String(evt?.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;

      const k = String(evt.key || "").toLowerCase();
      const isLaneKey = k === "w" || k === "y" || k === "s" || k === "d";
      if (!isLaneKey) return;

      const sec = sections[0];
      const ent = sec?.entity;
      if (!ent) return;

      const ds = ent?.entityCollection?.owner;
      const layerNameLower = String(ds?.layerName || "").toLowerCase();
      if (!layerNameLower.includes("lane_marker")) return;

      evt.preventDefault?.();
      evt.stopPropagation?.();

      if (!ent.properties) ent.properties = new PropertyBag({});

      if (k === "w") ent.properties.color = "white";
      if (k === "y") ent.properties.color = "yellow";
      if (k === "s") ent.properties.subtype = "solid";
      if (k === "d") ent.properties.subtype = "dashed";

      applyLaneMarkerVisual(ent);

      setSections(buildSections([ent]));
      viewer.scene.requestRender?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [viewer, isOpen, sections]);

  const display = isOpen && sections.length ? "block" : "none";
  const maxH = useMemo(() => "calc(100vh - 250px)", []);

  return (
    <Box
      display={display}
      position="absolute"
      top="80px"
      right="65px"
      zIndex={2000}
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      boxShadow="md"
      maxH={maxH}
      overflowY="auto"
      maxW="680px"
      minW="250px"
      p="10px"
      pt="34px"
      whiteSpace="normal"
    >
      <IconButton aria-label="Close" size="sm" variant="ghost" position="absolute" top="6px" right="6px" onClick={close}>
        ✖
      </IconButton>

      <Accordion.Root multiple collapsible value={expanded} onValueChange={(e) => setExpanded(e.value)}>
        {sections.map((sec) => (
          <Accordion.Item key={sec.sectionId} value={sec.sectionId}>
            <Accordion.ItemTrigger
              style={{
                width: "100%",
                marginTop: "10px",
                border: "1px solid #e2e8f0",
                background: "#f7fafc",
                borderRadius: "8px",
                padding: "10px",
                cursor: "pointer",
              }}
            >
              <Flex w="100%" align="center" justify="space-between" gap="12px">
                <Box textAlign="left" flex="1" minW="0">
                  <Heading size="sm" color="gray.800" wordBreak="break-word">
                    Layer: {sec.layerName}
                  </Heading>

                  <Flex mt="6px" align="center" gap="8px" wrap="wrap">
                    <IconButton
                      as="span"
                      role="button"
                      tabIndex={0}
                      colorPalette="red"
                      aria-label="Delete"
                      size="xs"
                      variant="outline"
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        onDelete(sec.sectionId);
                      }}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          ev.stopPropagation();
                          onDelete(sec.sectionId);
                        }
                      }}
                    >
                      <LuTrash />
                    </IconButton>
                    <IconButton
                      as="span"
                      role="button"
                      tabIndex={0}
                      aria-label="Toggle visibility"
                      size="xs"
                      variant="outline"
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        onToggleVisibility(sec);
                      }}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          ev.stopPropagation();
                          onToggleVisibility(sec);
                        }
                      }}
                    >
                      {(() => {
                        const dsOwner = sec.entity?.entityCollection?.owner;
                        const item =
                          (Array.isArray(mapObjects) && mapObjects.find((o) => o?.dataSource === dsOwner || o?.osmDataSource === dsOwner)) ||
                          (Array.isArray(mapObjects) && mapObjects.find((o) => removeFileExtension(o?.file_name || "") === sec.layerName)) ||
                          null;

                        return item?.visible ? <LuEye /> : <LuEyeOff />;
                      })()}
                    </IconButton>

                    <Text fontSize="sm" color="gray.700">
                      Length: {sec.lengthMeters.toFixed(2)} meters
                    </Text>
                  </Flex>
                </Box>

                <Accordion.ItemIndicator />
              </Flex>
            </Accordion.ItemTrigger>

            <Accordion.ItemContent>
              <Accordion.ItemBody style={{ paddingTop: "10px" }}>
                <Table.Root size="sm" variant="outline" width="100%">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader whiteSpace="nowrap" textAlign="left">
                        Key
                      </Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="left">Value</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {sec.rows.map((r) => {
                      const isCopyRow = r.key === "id" || r.key === "right_line_id" || r.key === "left_line_id";
                      const copyText = isCopyRow ? String(r.value ?? "") : "";

                      return (
                        <Table.Row key={r.key}>
                          <Table.Cell fontWeight="bold" whiteSpace="nowrap" verticalAlign="top">
                            {r.key}
                          </Table.Cell>

                          <Table.Cell style={{ wordBreak: "break-word" }}>
                            {isCopyRow ? (
                              <Clipboard.Root
                                value={copyText}
                                onClick={() => {
                                  showToast("Copied", Type.NOTIFICATION.SUCCESS);
                                }}
                              >
                                <Flex align="center" gap="8px" wrap="wrap">
                                  <Clipboard.ValueText />
                                  <Clipboard.Trigger asChild>
                                    <IconButton size="xs" variant="outline">
                                      <Clipboard.Indicator />
                                    </IconButton>
                                  </Clipboard.Trigger>
                                </Flex>
                              </Clipboard.Root>
                            ) : (
                              <ValueView value={r.value} />
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table.Root>
              </Accordion.ItemBody>
            </Accordion.ItemContent>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </Box>
  );
});
